import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { canAdminDocuments } from "@/lib/document/documentRoles";
import { listOrgFormTemplates } from "@/lib/opportunity/orgFormTemplateClient";
import type { OrgFormTemplate } from "@/lib/opportunity/orgFormTemplateTypes";
import {
  listDocFormBridgeLinks,
  upsertDocFormBridge,
  type DocFormTemplateLink,
} from "@/lib/document-builder/bridge/orgFormBridgeClient";
import { SectionCard } from "@/components/common/SectionCard";

type Props = {
  documentId: string;
  publishedVersionId: string | null;
  hasDraft?: boolean;
  onChanged?: () => void;
};

/** Document-side org form bridge — link Fulfillment form templates to this published document. */
export function DocumentFormBridgeSection(props: Props) {
  const { documentId, publishedVersionId, hasDraft = false, onChanged } = props;
  const auth = useAuth();
  const canAdmin = canAdminDocuments(auth);

  const [templates, setTemplates] = useState<OrgFormTemplate[]>([]);
  const [links, setLinks] = useState<DocFormTemplateLink[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const [tplR, linksR] = await Promise.all([
      listOrgFormTemplates(),
      listDocFormBridgeLinks(documentId),
    ]);
    setLoading(false);
    if (tplR.ok) {
      const active = tplR.data.templates.filter((t) => t.is_active !== false);
      setTemplates(active);
      setSelectedTemplateId((prev) => {
        if (prev && active.some((t) => t.id === prev)) return prev;
        return active[0]?.id ?? "";
      });
    }
    if (linksR.ok) setLinks(linksR.data.links);
  }, [documentId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const linkedTemplateIds = new Set(links.map((l) => l.form_template_id));
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? null;

  async function handleLink() {
    if (!publishedVersionId) {
      setMsg({ ok: false, text: "Publish this document before linking form templates." });
      return;
    }
    if (!selectedTemplateId) {
      setMsg({ ok: false, text: "Choose a form template." });
      return;
    }
    setSaving(true);
    setMsg(null);
    const r = await upsertDocFormBridge({
      form_template_id: selectedTemplateId,
      document_id: documentId,
      published_version_id: publishedVersionId,
    });
    setSaving(false);
    if (!r.ok) {
      setMsg({ ok: false, text: r.error });
      return;
    }
    setMsg({
      ok: true,
      text: linkedTemplateIds.has(selectedTemplateId)
        ? "Bridge updated."
        : "Form template linked — Fulfillment fill uses Document Builder export.",
    });
    await reload();
    onChanged?.();
  }

  return (
    <SectionCard
      title="Org form bridge (Phase B)"
      description="Link Fulfillment form templates to this published document. When linked, fill uses Document Builder variables and PDF export instead of legacy opp_form rendering."
    >
      {!publishedVersionId ? (
        <p className="text-sm text-amber-800 dark:text-amber-200">
          {hasDraft
            ? "Publish the current draft in Versions above, then link org form templates here."
            : "Workflow: add blocks → Save first draft version → Publish → link forms here (or from Forms & formats)."}
        </p>
      ) : loading ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading…</p>
      ) : templates.length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-zinc-300">
          No org form templates yet — create one under Forms &amp; formats → Forms.
        </p>
      ) : (
        <div className="space-y-3">
          {links.length > 0 ? (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                Linked templates
              </p>
              <ul className="mt-1 space-y-1">
                {links.map((link) => {
                  const tpl = templates.find((t) => t.id === link.form_template_id);
                  return (
                    <li
                      key={link.id}
                      className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-sm text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-100"
                    >
                      {tpl?.display_name ?? link.form_template_id.slice(0, 8)}
                      <span className="ml-2 text-xs opacity-70">
                        v{link.published_version_id.slice(0, 8)}…
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-zinc-300">
              Not linked — Fulfillment uses legacy form fill until a template is linked.
            </p>
          )}

          {canAdmin ? (
            <label className="block text-sm">
              <span className="font-medium text-gray-700 dark:text-zinc-300">Form template</span>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              >
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.display_name}
                    {linkedTemplateIds.has(tpl.id) ? " (linked)" : ""}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              Administrator access is required to create or update bridge links.
            </p>
          )}
        </div>
      )}

      {canAdmin && publishedVersionId && templates.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          {msg ? (
            <span className={msg.ok ? "text-xs text-emerald-600" : "text-xs text-red-600"}>
              {msg.text}
            </span>
          ) : null}
          <button
            type="button"
            disabled={saving || loading || !selectedTemplateId}
            onClick={() => void handleLink()}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving
              ? "Linking…"
              : selectedTemplate && linkedTemplateIds.has(selectedTemplate.id)
                ? "Update link"
                : "Link template"}
          </button>
        </div>
      ) : null}
    </SectionCard>
  );
}
